import Source from "../sources/Source";
import SourceTextCache from "../sources/SourceTextCache";
import VersionRenderer from "./VersionRenderer";
import VersionRenderStyle from "./VersionRenderStyle";
import VersionStyle from "./VersionStyle";
import VersionStyles from "./VersionStyles";
import WordRenderer from "./WordRenderer";
import WordStyles from "./WordStyles";

interface Line {
    source: Source;
    style?: VersionStyle;
    wordSeparator?: string;
}

export default class InterlinearRenderer implements VersionRenderer {

    private lines: Line[] = [];

    public addLine(line: Line): InterlinearRenderer {
        this.lines.push(line);
        return this;
    }

    public render(verseIndex: number, sourceTextCache: SourceTextCache, style?: VersionRenderStyle): string {
        const matrix = this.getTableMatrix(verseIndex, sourceTextCache, style || {});
        const columnCount = matrix.length > 0 ? matrix[0].length : 0;

        let text = '';
        for (let columnIndex = 0; columnIndex < columnCount; columnIndex++) {
            text += '<table>';
            for (const row of matrix) {
                text += `<tr><td>${row[columnIndex]}</td></tr>`;
            }
            text += '</table>';
        }

        if (text !== '') {
            text = `<div class="_InterlinearVerse">${text}</div>`;
        }
        return text;
    }

    private getTableMatrix(verseIndex: number, sourceTextCache: SourceTextCache, renderStyle: VersionRenderStyle): string[][] {
        const lineWords = this.lines.map(line => {
            const text = sourceTextCache.getText(verseIndex, line.source.getIndex());
            return text ? text.split(line.wordSeparator || ' ') : [];
        });
        const maxWordCount = lineWords.reduce((max, value) => value.length > max ? value.length : max, 0);

        return this.lines.map((line, index) => {
            const words = lineWords[index];
            const style = VersionStyles.mergeStyles(line.style || {}, renderStyle);
            const wordRenderer = new WordRenderer({
                classes: style.classes,
                emptyText: '&nbsp;',
                interlinearNumberStyle: WordStyles.copyVersionInterlinearNumberStyle(style),
                lexiconLookup: style.allowLexiconLookup ? {
                    sourceIndex: line.source.getIndex(),
                    verseIndex
                } : undefined,
                partJoiner: style.wordPartJoiner,
                partSeparator: style.wordPartSeparator,
                partTransformer: style.wordPartTransformer,
                tooltipGenerator: style.wordTooltipGenerator
            });

            const columns = [];
            for (let wordIndex = 0; wordIndex < maxWordCount; wordIndex++) {
                const word = words[wordIndex] || '';
                columns.push(wordRenderer.renderWord(word, wordIndex));
            }
            return columns;
        });
    }
}
